import React from 'react';
import { DollarSign, ShoppingBag, TrendingUp, ArrowUp, ArrowDown, AlertCircle } from 'lucide-react';
import type { SpendingSummaryCardsProps, SpendingAnalyticsSummary } from '../../types/analytics';

/**
 * Format a number as currency.
 *
 * Note: Backend stores amounts in dollars (Decimal 10,2), not cents.
 * So 267.95 is $267.95, not $2.6795.
 */
function formatCurrency(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format date for display (e.g., "Jan 1")
 * Handles date-only strings (YYYY-MM-DD) by treating them as local dates
 */
function formatDateShort(dateString: string): string {
  try {
    // Parse YYYY-MM-DD format as local date to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return dateString;
  }
}

/**
 * Individual metric card component
 */
interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  testIdPrefix: string;
  borderColor: string;
  isCount?: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  icon,
  testIdPrefix,
  borderColor,
  isCount = false,
}) => {
  const titleSlug = testIdPrefix.toLowerCase().replace(/\s+/g, '-');

  return (
    <div
      data-testid={`${titleSlug}-card`}
      className={`bg-white dark:bg-cap-dark rounded-lg shadow-sm p-4 border-l-4 ${borderColor}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</h3>
          <p
            className="mt-1 text-2xl font-semibold text-gray-900 dark:text-warm-white"
            aria-label={`${title} value`}
          >
            {value}
          </p>
        </div>
        <div
          data-testid={`${titleSlug}-icon`}
          className="p-3 bg-gray-50 dark:bg-gray-900 rounded-full"
        >
          {icon}
        </div>
      </div>
    </div>
  );
};

/**
 * Loading skeleton for metric card
 */
const MetricCardSkeleton: React.FC = () => (
  <div
    data-testid="metric-card-skeleton"
    className="bg-white dark:bg-cap-dark rounded-lg shadow-sm p-4 border-l-4 border-gray-200 dark:border-gray-700 animate-pulse"
  >
    <div className="flex items-center justify-between">
      <div>
        <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="mt-2 h-8 w-32 bg-gray-200 dark:bg-gray-700 rounded" />
      </div>
      <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-full">
        <div className="h-6 w-6 bg-gray-200 dark:bg-gray-700 rounded" />
      </div>
    </div>
  </div>
);

/**
 * SpendingSummaryCards Component
 *
 * Displays key spending metrics in a responsive card grid.
 * Shows: Total Spent, Total Purchases, Average Price, Highest Purchase, Lowest Purchase
 */
const SpendingSummaryCards: React.FC<SpendingSummaryCardsProps> = ({
  summary,
  loading,
  error,
}) => {
  // Loading state
  if (loading) {
    return (
      <div role="status" aria-busy="true" aria-label="Loading spending summary">
        <div
          data-testid="summary-cards-container"
          className="grid grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {[1, 2, 3, 4, 5].map((i) => (
            <MetricCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        role="alert"
        className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3"
      >
        <AlertCircle
          data-testid="error-icon"
          className="w-6 h-6 text-red-500 flex-shrink-0"
        />
        <p className="text-red-700 dark:text-red-400">{error}</p>
      </div>
    );
  }

  // Empty state
  if (!summary) {
    return (
      <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-6 text-center">
        <p className="text-gray-500 dark:text-gray-400">No data available</p>
      </div>
    );
  }

  const { currency, period } = summary;

  return (
    <div>
      {/* Period header */}
      <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
        <span className="font-medium">{period.label}</span>
        {period.start_date && period.end_date && (
          <span className="ml-2">
            ({formatDateShort(period.start_date)} - {formatDateShort(period.end_date)})
          </span>
        )}
      </div>

      {/* Metrics grid */}
      <div
        data-testid="summary-cards-container"
        className="grid grid-cols-2 lg:grid-cols-4 gap-4"
      >
        <MetricCard
          title="Total Spent"
          value={formatCurrency(summary.total_spent, currency)}
          icon={<DollarSign className="w-6 h-6 text-primary-500" />}
          testIdPrefix="total-spent"
          borderColor="border-blue-500"
        />

        <MetricCard
          title="Total Purchases"
          value={summary.total_purchases.toString()}
          icon={<ShoppingBag className="w-6 h-6 text-green-500" />}
          testIdPrefix="total-purchases"
          borderColor="border-green-500"
          isCount
        />

        <MetricCard
          title="Average Price"
          value={formatCurrency(summary.average_price, currency)}
          icon={<TrendingUp className="w-6 h-6 text-purple-500" />}
          testIdPrefix="average-price"
          borderColor="border-purple-500"
        />

        <MetricCard
          title="Highest Purchase"
          value={formatCurrency(summary.highest_purchase, currency)}
          icon={<ArrowUp className="w-6 h-6 text-orange-500" />}
          testIdPrefix="highest-purchase"
          borderColor="border-orange-500"
        />

        <MetricCard
          title="Lowest Purchase"
          value={formatCurrency(summary.lowest_purchase, currency)}
          icon={<ArrowDown className="w-6 h-6 text-teal-500" />}
          testIdPrefix="lowest-purchase"
          borderColor="border-teal-500"
        />
      </div>
    </div>
  );
};

export default SpendingSummaryCards;
